import { useState, useEffect, useCallback, useRef } from "react";
import { FormattedNumber } from "react-intl";
import moment from "moment";
import styled from "styled-components";
import ICatalog_Item from "@models/ICatalog_Item";
import IVendorModel from "@models/IVendorModel";
import IGetVendorsQuery from "@models/DTO/IGetVendorsQuery";
import { ICustomerInventoryItem } from "@models/DTO/IOnGetInventoryByCustomerDTO";
import { useAppContextNonNull } from "@contexts/AppContext";
import InventoryService from "@services/InventoryService";
import VendorService from "@services/VendorService";
import UIService from "@services/UIService";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { OwIconButton } from "@components/shared/OwIconButton/OwIconButton";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import PaginationBar from "@components/shared/PaginationBar/PaginationBar";
import { SearchSection } from "@components/shared/SearchSection/SearchSection";
import { InventoryItemReservedQtyOnHandDetailSidebar } from "../InventoryItemReservedQtyOnHandDetailSidebar/InventoryItemReservedQtyOnHandDetailSidebar";
import { InventoryItemBackorderedQtyDetailSidebar } from "../InventoryItemBackorderedQtyDetailSidebar/InventoryItemBackorderedQtyDetailSidebar";
import "./InventoryByCustomerView.scss";

const DEFAULT_LIMIT = 25;

export const InventoryByCustomerView = () => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();

    const [foundVendors, setFoundVendors] = useState<IVendorModel[]>([]);
    const [selectedVendor, setSelectedVendor] = useState<IVendorModel | null>(null);
    const [resetTypeahead, setResetTypeahead] = useState(false);
    const [items, setItems] = useState<ICustomerInventoryItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    // Guards against out-of-order responses: only the most recent request may update state.
    const fetchRequestIdRef = useRef(0);

    const findVendorsByName = async (typeahead: string) => {
        const query: IGetVendorsQuery = {
            name: typeahead,
            nameGuidOnly: "true",
        };
        const data = await VendorService.getVendors(query, userModel, supplierModel);
        const vendors = data.supplierVendorTokens.filter((t) => !!t.Vendor).map((t) => t.Vendor!);
        setFoundVendors(vendors);
    };

    const handleVendorSelected = (vendor: IVendorModel) => {
        fetchRequestIdRef.current++;
        setSelectedVendor(vendor);
        setItems([]);
        setTotalCount(0);
        setOffset(0);
    };

    const handleReset = () => {
        fetchRequestIdRef.current++;
        setSelectedVendor(null);
        setFoundVendors([]);
        setResetTypeahead(true);
        setItems([]);
        setTotalCount(0);
        setOffset(0);
        setIsLoading(false);
    };

    const fetchInventory = useCallback(async () => {
        if (!selectedVendor?.guid) {
            return;
        }

        const requestId = ++fetchRequestIdRef.current;
        setIsLoading(true);

        try {
            const result = await InventoryService.getInventoryByCustomer(
                {
                    vendorGuid: selectedVendor.guid,
                    limit,
                    page: offset,
                },
                userModel,
                supplierModel
            );

            if (requestId !== fetchRequestIdRef.current) {
                return;
            }

            if (result.success && result.data) {
                setItems(result.data.items);
                setTotalCount(result.data.totalCount);
            }
        } catch (error) {
            console.error("Error fetching inventory by customer:", error);
        } finally {
            if (requestId === fetchRequestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [selectedVendor?.guid, limit, offset, userModel, supplierModel]);

    useEffect(() => {
        fetchInventory();
    }, [fetchInventory]);

    const handleExportCsv = async () => {
        if (!selectedVendor?.guid) {
            return;
        }

        setIsExporting(true);

        try {
            const result = await InventoryService.getInventoryByCustomerCsv(selectedVendor.guid, userModel, supplierModel);

            if (result.success && result.csvData && result.filename) {
                UIService.downloadCSVData(result.csvData, result.filename);
            }
        } catch (error) {
            console.error("Error exporting inventory by customer:", error);
        } finally {
            setIsExporting(false);
        }
    };

    const handlePaginationChanged = (newOffset: number, newLimit: number) => {
        setOffset(newOffset);
        setLimit(newLimit);
    };

    const closeSidebar = () => {
        uiContext.toggleSidebar(false);
        uiContext.setSidebarComponent(null);
    };

    const buildCatalogItem = (item: ICustomerInventoryItem) => {
        return { guid: item.catalogItemGuid, sku: item.sku, description: item.name } as ICatalog_Item;
    };

    const openReservedSidebar = (item: ICustomerInventoryItem) => {
        if (!selectedVendor) {
            return;
        }

        uiContext.setSidebarComponent(
            <InventoryItemReservedQtyOnHandDetailSidebar
                catalogItem={buildCatalogItem(item)}
                initialVendorFilter={{ vendorGuid: selectedVendor.guid, vendorName: selectedVendor.name }}
                closeFn={closeSidebar}
            />
        );
        uiContext.toggleSidebar(true);
    };

    const openBackorderedSidebar = (item: ICustomerInventoryItem) => {
        if (!selectedVendor) {
            return;
        }

        uiContext.setSidebarComponent(
            <InventoryItemBackorderedQtyDetailSidebar
                catalogItem={buildCatalogItem(item)}
                initialVendorFilter={{ vendorGuid: selectedVendor.guid, vendorName: selectedVendor.name }}
                closeFn={closeSidebar}
            />
        );
        uiContext.toggleSidebar(true);
    };

    const renderImage = (item: ICustomerInventoryItem) => {
        if (!item.imageUrl) {
            return "--";
        }

        return (
            <img
                src={item.imageUrl}
                alt={item.sku}
                className="item-thumbnail"
                onError={(e) => {
                    e.currentTarget.style.display = "none";
                }}
            />
        );
    };

    const renderReserved = (item: ICustomerInventoryItem) => {
        return (
            <div className="d-flex justify-content-end">
                <FormattedNumber value={item.qtyReserved} minimumFractionDigits={supplierModel.qty_precision} />
                {item.qtyReserved > 0 && (
                    <OwIconButton
                        iconClass="fas fa-magnifying-glass ms-2"
                        iconColorClass="text-primary"
                        fn={() => {
                            openReservedSidebar(item);
                        }}
                        title="View Reserved Qty On Hand"
                    />
                )}
            </div>
        );
    };

    const renderBackordered = (item: ICustomerInventoryItem) => {
        return (
            <div className="d-flex justify-content-end">
                <FormattedNumber value={item.qtyBackordered} minimumFractionDigits={supplierModel.qty_precision} />
                {item.qtyBackordered > 0 && (
                    <OwIconButton
                        iconClass="fas fa-magnifying-glass ms-2"
                        iconColorClass="text-primary"
                        fn={() => {
                            openBackorderedSidebar(item);
                        }}
                        title="View Qty Backordered"
                    />
                )}
            </div>
        );
    };

    const formatDate = (dateString: string | null) => {
        return dateString ? moment(dateString).format("MM/DD/YYYY") : "--";
    };

    let tableClass = "table";

    if (!UIService.isMainViewLight()) {
        tableClass += " table-dark";
    }

    const renderBody = () => {
        if (!selectedVendor) {
            return (
                <div className="empty-state text-center py-5">
                    <i className="fas fa-inbox fa-3x mb-3"></i>
                    <div>
                        <OwLabel title={"select_customer_to_view_inventory"} />
                    </div>
                </div>
            );
        }

        if (isLoading) {
            return (
                <div className="loading-container text-center py-5">
                    <i className="fas fa-spinner fa-spin fa-2x"></i>
                </div>
            );
        }

        if (!items.length) {
            return (
                <div className="empty-state text-center py-5">
                    <i className="fas fa-inbox fa-3x mb-3"></i>
                    <div>
                        <OwLabel title={"no_items_found"} />
                    </div>
                </div>
            );
        }

        return (
            <>
                <div className="table-responsive d-none d-md-block">
                    <table className={tableClass}>
                        <thead>
                            <tr>
                                <th></th>
                                <th>
                                    <OwLabel title={"sku"} />
                                </th>
                                <th>
                                    <OwLabel title={"name"} />
                                </th>
                                <th>
                                    <OwLabel title={"uom"} />
                                </th>
                                <th className="text-end">
                                    <OwLabel title={"reserved"} />
                                </th>
                                <th className="text-end">
                                    <OwLabel title={"backordered"} />
                                </th>
                                <th>
                                    <OwLabel title={"last_order_date"} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.catalogItemGuid}>
                                    <td>{renderImage(item)}</td>
                                    <td>{item.sku}</td>
                                    <td>{item.name}</td>
                                    <td>{item.uom || "--"}</td>
                                    <td className="text-end">{renderReserved(item)}</td>
                                    <td className="text-end">{renderBackordered(item)}</td>
                                    <td>{formatDate(item.lastOrderDate)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="d-md-none">
                    {items.map((item) => (
                        <MobileRowWrapper key={item.catalogItemGuid}>
                            <div className="d-flex justify-content-between">
                                <MobileNameWrapper>{item.name}</MobileNameWrapper>
                                {renderImage(item)}
                            </div>
                            <div className="mb-2">{item.sku}</div>
                            <div className="d-flex justify-content-between">
                                <div>
                                    <OwLabel title={"reserved"} className={"me-2"} postText={":"} isBold={true} />
                                    {renderReserved(item)}
                                </div>
                                <div>
                                    <OwLabel title={"backordered"} className={"me-2"} postText={":"} isBold={true} />
                                    {renderBackordered(item)}
                                </div>
                            </div>
                            <div>
                                <OwLabel title={"last_order_date"} className={"me-2"} postText={":"} isBold={true} />
                                {formatDate(item.lastOrderDate)}
                            </div>
                        </MobileRowWrapper>
                    ))}
                </div>

                {totalCount > 0 && (
                    <PaginationBar
                        totalRecords={totalCount}
                        limit={limit}
                        offset={offset}
                        onPaginationChanged={handlePaginationChanged}
                        isActive={isLoading}
                    />
                )}
            </>
        );
    };

    return (
        <div className="InventoryByCustomerView">
            <div className="mb-2">
                <SearchSection>
                    <div className="row align-items-end">
                        <div className="mb-2 mb-md-0 col-12 col-sm-6 col-md-4 col-lg-3">
                            <OwLabel title={"customer"} />
                            <TypeaheadFn
                                lookupFn={findVendorsByName}
                                availableOptions={foundVendors}
                                selectedOption={selectedVendor}
                                selectedOptionFn={handleVendorSelected}
                                clearSelectionFn={handleReset}
                                resetTypeahead={resetTypeahead}
                                resetTypeaheadCallbackFn={() => setResetTypeahead(false)}
                                titleProp="name"
                                subTitleProp="customer_internal_id"
                                isScrollable={true}
                                placeholder={"search_by_customer"}
                            />
                        </div>
                        <div className="col-auto mt-2 mt-md-0">
                            <OwButton
                                className={"btn-sm btn-danger"}
                                fn={handleReset}
                                title={"reset"}
                                disabled={isLoading || !selectedVendor}
                            />
                        </div>
                        <div className="col-auto ms-auto mt-2 mt-md-0">
                            <OwButton
                                fn={handleExportCsv}
                                disabled={isExporting || isLoading || totalCount === 0}
                                title={isExporting ? "exporting" : "export_csv"}
                                leftIconClass="fas fa-file-csv"
                                className="btn-primary btn-sm"
                            />
                        </div>
                    </div>
                </SearchSection>
            </div>

            {renderBody()}
        </div>
    );
};

const MobileRowWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0.7rem;

    &:nth-child(odd) {
        background-color: var(--secondary-bg-color);
    }
`;

const MobileNameWrapper = styled.div`
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;
